import {
  Component,
  signal,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core'

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatDividerModule } from '@angular/material/divider'
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatRadioModule } from '@angular/material/radio'
import { Dialog } from '@angular/cdk/dialog'
import { firstValueFrom } from 'rxjs'

import { SpinnerService } from '../../../../../services/spinner.service'
import { DialogService } from '../../../../../services/dialog.service'
import { ErrorService } from '../../../../../services/error.service'
import { TranslationService } from '../../../../../services/translation.service'
import { FprTemplate } from '../../../../../types/fprTemplates'
import { ReportService } from '../../../../../services/report.service'
import { ApiError } from '../../../../../types/apiError'
import { ImportReportResponse } from '../../../../../types/reports'

type SubmitResult = ImportReportResponse
type BottomSheetData = {
  templates: FprTemplate[]
  year: number | null
  reportId?: string | null
  isEditMode?: boolean
}

@Component({
  selector: 'app-fpr-import-sheet',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatCheckboxModule,
    MatRadioModule,
  ],
  templateUrl: './fpr-import-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './fpr-import-sheet.component.scss',
})
export class FprImportSheetComponent {
  private readonly fb = inject(FormBuilder)
  private readonly sheetRef =
    inject<
      MatBottomSheetRef<FprImportSheetComponent, SubmitResult | undefined>
    >(MatBottomSheetRef)
  private readonly data = inject<BottomSheetData | null>(
    MAT_BOTTOM_SHEET_DATA,
    { optional: true },
  )

  private readonly reportService = inject(ReportService)
  private readonly spinner = inject(SpinnerService)
  private readonly dialog = inject(Dialog)
  private readonly dialogService = inject(DialogService)
  private readonly errorService = inject(ErrorService)
  public readonly translationService = inject(TranslationService)

  readonly isEditMode = this.data?.isEditMode ?? false
  readonly showMergeOptions = !!this.data?.reportId

  templates: Array<FprTemplate> = this.data?.templates ?? []

  readonly tags = input<string[]>([])

  readonly maxFileSizeBytes = 10 * 1024 * 1024 // 10 MB
  // Accept only Excel .xlsx
  readonly acceptAttr =
    '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

  readonly selectedFile = signal<File | null>(null)
  readonly fileError = signal<string | null>(null)

  form = this.fb.group({
    templateId: ['', Validators.required],
    file: [null as File | null, Validators.required],
    mergeMode: this.fb.control<'overwrite' | 'add'>('overwrite', {
      nonNullable: true,
    }),
  })

  /**
   * Handles native file input changes with validation for:
   * - extension / MIME type
   * - file size
   */
  onFileChange(ev: Event): void {
    const input = ev.target as HTMLInputElement
    const file = input.files && input.files[0] ? input.files[0] : null
    this.fileError.set(null)

    if (!file) {
      this.selectedFile.set(null)
      this.form.patchValue({ file: null })
      this.form.get('file')?.markAsTouched()
      this.form.get('file')?.updateValueAndValidity()
      return
    }

    if (file.size > this.maxFileSizeBytes) {
      this.fileError.set(this.translationService.getFileTooLargeText())
      this.selectedFile.set(null)
      this.form.patchValue({ file: null })
      this.form.get('file')?.updateValueAndValidity()
      return
    }

    this.selectedFile.set(file)
    this.form.patchValue({ file })
    this.form.get('file')?.updateValueAndValidity()
  }

  /** Resets the UI back to defaults */
  reset(): void {
    this.form.reset({ templateId: '', file: null, mergeMode: 'overwrite' })
    this.selectedFile.set(null)
    this.fileError.set(null)
  }

  /**
   * Validates the form and dismisses the sheet with the payload.
   * The caller (parent component) performs the upload and handles errors.
   */
  async submit(): Promise<void> {
    if (this.form.invalid || !this.selectedFile()) {
      this.form.markAllAsTouched()
      return
    }

    try {
      this.spinner.startSpinner()
      const { templateId, mergeMode } = this.form.getRawValue()
      const file = this.selectedFile() as File

      const year = typeof this.data?.year === 'number' ? this.data.year : null
      const reportId = this.data?.reportId ?? null

      if (!reportId && typeof year !== 'number') {
        console.error('Import year was not provided to the sheet.')
        return
      }

      const resp = await firstValueFrom(
        this.reportService.importReportFromSpreadsheet(
          templateId as string,
          year,
          file,
          mergeMode,
          this.tags(),
          reportId ?? undefined,
        ),
      )
      this.sheetRef.dismiss(resp as ImportReportResponse)
    } catch (err) {
      this.errorService.handleErr(
        this.dialogService,
        err as ApiError,
        this.dialog,
      )
    } finally {
      this.spinner.stopSpinner()
    }
  }

  /** Closes the sheet without returning data */
  cancel(): void {
    this.sheetRef.dismiss(undefined)
  }
}
